<script setup lang="ts">
import { Folder, FileText, LoaderCircle, AlertCircle } from 'lucide-vue-next'
import { formatRelativeTimeCompact } from '~/utils/relativeTime'
import { fileExt, extBadgeClass } from '~/lib/fileType'
import { storeToRefs } from 'pinia'
import type { ItemRow } from '~~/shared/items/itemRow'
import { isItemPending } from '~~/shared/items/itemPending'
import { useDocumentsStore } from '~/stores/documents'
import { useFolderDrop } from '~/composables/useFolderDrop'

/** Finder-style icon/grid rendering of a folder level. List behaviour (selection, open) mirrors
 *  BrowserItemList, but laid out as cards. Used by the /app/documents finder's grid view. */
const props = defineProps<{ items: ItemRow[] }>()

const emit = defineEmits<{ contextmenu: [e: MouseEvent, item: ItemRow] }>()

const { t, locale } = useI18n()
const router = useRouter()
const documents = useDocumentsStore()
const { selectedIds, dragItemId, dropTargetId } = storeToRefs(documents)
const { selectOnly, toggleSelected, openFolder, startDrag, endDrag } = documents
// Identical drop rules to the list view — shared, not reimplemented.
const { onFolderDragOver, onFolderDragLeave, onFolderDrop } = useFolderDrop()

function isSelected(id: string) {
  return selectedIds.value.has(id)
}

function onClick(e: MouseEvent, item: ItemRow) {
  if (e.metaKey || e.ctrlKey) toggleSelected(item.id)
  else selectOnly(item.id)
}

// MAR-246: an image with a generated thumbnail shows it instead of the icon. The thumbnail is a small
// WebP served by the item's own org-scoped file endpoint — never the original, which may be a multi-MB
// photo or a HEIC no desktop browser renders. `updated_at` busts the browser cache after a re-read.
// A tile whose thumbnail fails to load drops back to the icon for the rest of this mount.
const brokenThumbs = ref(new Set<string>())

function thumbUrl(item: ItemRow): string | null {
  if (item.type !== 'document' || !item.has_thumbnail || brokenThumbs.value.has(item.id)) return null
  return `/api/items/${item.id}/file?variant=thumb&v=${encodeURIComponent(item.updated_at)}`
}

function onThumbError(id: string) {
  brokenThumbs.value = new Set(brokenThumbs.value).add(id)
}

function onOpen(item: ItemRow) {
  if (item.type === 'folder') openFolder(item)
  else router.push(`/app/docs/${item.id}`)
}
</script>

<template>
  <div
    class="grid gap-2 p-3"
    style="grid-template-columns: repeat(auto-fill, minmax(130px, 1fr))"
  >
    <button
      v-for="item in props.items"
      :key="item.id"
      type="button"
      draggable="true"
      class="group relative flex flex-col items-center gap-2 rounded-lg border border-transparent p-3 text-center transition-colors hover:bg-accent-lighter focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      :class="[
        isSelected(item.id) ? 'bg-accent-surface border-accent-ink/40' : '',
        dropTargetId === item.id ? 'border-accent-ink bg-accent-surface ring-1 ring-accent-ink' : '',
        dragItemId === item.id ? 'opacity-50' : '',
      ]"
      @click="onClick($event, item)"
      @dblclick="onOpen(item)"
      @contextmenu.prevent="emit('contextmenu', $event, item)"
      @dragstart="startDrag(item, $event)"
      @dragend="endDrag"
      @dragover="onFolderDragOver($event, item)"
      @dragleave="onFolderDragLeave"
      @drop="onFolderDrop($event, item)"
    >
      <!-- Icon / thumbnail -->
      <div class="relative flex h-16 w-16 items-center justify-center">
        <Folder v-if="item.type === 'folder'" class="h-14 w-14 text-warning-ink" :fill="isSelected(item.id) ? 'currentColor' : 'none'" />
        <img
          v-else-if="thumbUrl(item)"
          :src="thumbUrl(item)!"
          alt=""
          loading="lazy"
          decoding="async"
          draggable="false"
          class="h-16 w-16 rounded border border-border object-cover"
          @error="onThumbError(item.id)"
        >
        <template v-else>
          <FileText class="h-14 w-14 text-muted-foreground" />
          <span
            v-if="fileExt(item.file_name)"
            class="absolute bottom-1 left-1/2 -translate-x-1/2 rounded px-1 text-[9px] font-semibold leading-tight"
            :class="extBadgeClass(fileExt(item.file_name))"
          >
            {{ fileExt(item.file_name) }}
          </span>
        </template>

        <LoaderCircle v-if="isItemPending(item.status)" class="absolute -right-1 -top-1 h-4 w-4 animate-spin text-primary" />
        <AlertCircle v-else-if="item.status === 'error'" class="absolute -right-1 -top-1 h-4 w-4 text-destructive" />
        <!-- Quota, not failure — see BrowserItemList. -->
        <AlertCircle
          v-else-if="item.status === 'quota_blocked'"
          class="absolute -right-1 -top-1 h-4 w-4 text-warning-ink"
          :title="t('documents.quotaBlockedTitle')"
        />
      </div>

      <!-- Name -->
      <span class="line-clamp-2 w-full break-words text-xs text-foreground" :title="item.name">
        {{ item.name }}
      </span>

      <!-- Meta -->
      <span class="text-[10px] text-muted-foreground">
        <template v-if="item.type === 'folder'">
          {{ item.child_count ?? 0 }} {{ t('documents.items') }}
        </template>
        <template v-else>
          {{ formatRelativeTimeCompact(item.updated_at, locale) }}
        </template>
      </span>
    </button>
  </div>
</template>
